import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { Zoom } from "@material-ui/core";

import {
  shouldUpdateEditSequenceList,
  selectEditSequenceList,
  shouldUpdateNewPinnedProducts,
  selectNumberOfPinnedProducts,
  selectSavedLockedProducts,
  selectSavedUnlockedProducts,
  selectLockedProducts,
  selectRemovedLockedProductsInCategory,
  selectStoredPinnedProductIds,
  selectStoredUnpinnedProductIds,
  selectProductListsFetched,
  selectPinnedProductIds,
  selectSavingCategoryIds,
  // selectHasNullSequences,
} from "../store/product-list/ProductListSelectors";
import {
  ProductSequenceParams,
  ProductSequenceData,
} from "../store/product-list/ProductListTypes";
import {
  updatePinnedProducts,
  updateProductSequenceList,
  revertProductSequence,
  lockProduct,
  unLockProduct,
  resetSavedLockedUnlockedProducts,
  resetStoredPinnedUnpinnedProductIds,
  setPinnedProducts,
} from "../store/product-list/ProductListActions";
import { selectCurrentStoreId } from "../store/store-list/StoreListSelectors";
import Tooltip from "./common/ToolTip";
import ButtonComponent from "./common/ButtonComponent";
import AppState from "store/AppState";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import {
  selectAccountFeatures,
  selectCallerAccountId,
  selectIsLinkedCategoryFeatureEnabled,
} from "store/app-config/AppConfigSelectors";
import { setModalState } from "store/modal/ModalActions";
import { LinkedCategoriesModalId } from "./linked-categories-modal/LinkedCategoriesModal";
interface Props {
  catalogId: string;
  categoryId: string;
  enableSaveMode: boolean;
  enableSyncMode: boolean;
  isCategoryInitialLoading: boolean;
}

const useStyles = () => {
  return {
    syncButton: {
      paddingLeft: 15,
    },
  };
};

const TopPanelSaveButton: React.FC<Props> = (props) => {
  const intl = useIntl();
  const { catalogId, categoryId, enableSaveMode, enableSyncMode, isCategoryInitialLoading } = props;
  const styles = useStyles();

  const dispatch = useDispatch();
  const shouldUpdateEditSequenceListFlag = useSelector(
    shouldUpdateEditSequenceList,
  );
  const shouldUpdateNewPinnedProductsFlag = useSelector(
    shouldUpdateNewPinnedProducts,
  );
  const numberOfPinnedProducts = useSelector(selectNumberOfPinnedProducts);
  const storeId = useSelector(selectCurrentStoreId);
  const products = useSelector(selectEditSequenceList);
  const savedLockedProducts = useSelector(selectSavedLockedProducts);
  const savedUnlockedProducts = useSelector(selectSavedUnlockedProducts);
  const lockedProducts = useSelector((state: AppState) =>
    selectLockedProducts(state, categoryId, catalogId),
  );
  const removedLockedProducts = useSelector((state: AppState) =>
    selectRemovedLockedProductsInCategory(state, categoryId, catalogId),
  );
  const storedPinnedProductIds = useSelector(selectStoredPinnedProductIds);
  const storedUnpinnedProductIds = useSelector(selectStoredUnpinnedProductIds);
  const pinnedProducts = useSelector(selectPinnedProductIds);
  const productsList = useSelector(selectProductListsFetched);
  const savingCategoryIds = useSelector(selectSavingCategoryIds);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isUnmanagedSupported = adapterInfo?.capabilities?.includes(
    "unmanaged-products",
  ) as boolean;
  const accountId = useSelector(selectCallerAccountId);
  const features = useSelector((state: AppState) =>
    selectAccountFeatures(state, accountId),
  );
  const isPinnedProductsFeatureEnabled = features?.["pinned-products"] ?? false;
  const isLinkedCategoryFeatureEnabled = useSelector(
    selectIsLinkedCategoryFeatureEnabled,
  );
  // const hasNullSequences = useSelector(selectHasNullSequences);

  const newPinnedProducts = products
    .map((product) => product.productId)
    .slice(0, numberOfPinnedProducts);

  const syncActionHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    dispatch(setModalState(LinkedCategoriesModalId, true));
  };

  const saveActionHandler = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    if (!shouldUpdateEditSequenceListFlag || enableSaveMode) {
      const productSequencesToUpdate: ProductSequenceParams[] = [];
      const updatedProductList: ProductSequenceData[] = [];
      let prevSequence = 0;
      const productIdsArray = products.map((product) => {
        return product.productId;
      });
      const isAllPinnedProductsChangedToLocked =
        Array.isArray(savedLockedProducts) && savedLockedProducts.length > 0
          ? savedLockedProducts.every((lockedProduct) =>
              pinnedProducts.includes(lockedProduct.productId),
            )
          : false;
      if (
        isPinnedProductsFeatureEnabled &&
        (storedPinnedProductIds.length > 0 ||
          storedUnpinnedProductIds.length > 0 ||
          (isAllPinnedProductsChangedToLocked &&
            storedPinnedProductIds.length === 0))
      ) {
        // Reorder storedPinnedProductIds based on their order in products list
        const reorderedPinnedProductIds = productsList
          .map((product) => product.productId)
          .filter((productId) => storedPinnedProductIds.includes(productId));

        const pinnedProductsPayload = {
          catalogId: catalogId,
          categoryId: categoryId,
          pinnedProductIds:
            reorderedPinnedProductIds.length > 0
              ? reorderedPinnedProductIds
              : [],
          sortOption: isUnmanagedSupported ? "unmanaged" : "manual",
        };
        dispatch(setPinnedProducts(pinnedProductsPayload));
      }

      // Filter out locked products that changed position or are already in lockCategory
      const filteredLockedProducts = savedLockedProducts?.filter(
        (lockedProduct) => {
          // Find product index and adjust for content slots before this position
          const productIndex = products.findIndex(
            (p) => p.productId === lockedProduct.productId,
          );

          const adjustedIndex = productIndex + 1;

          // Check if product is already in lockCategory
          const isAlreadyLocked = lockedProducts?.some(
            (lock) =>
              lock.productId === lockedProduct.productId &&
              lock.categoryId === categoryId &&
              lock.catalogId === catalogId,
          );

          // Only keep products that maintained their original sequence and aren't already locked
          return adjustedIndex === lockedProduct.position && !isAlreadyLocked;
        },
      );
      const filteredRemovedLockedProducts = savedUnlockedProducts?.filter(
        (unlockedProduct) => {
          return !removedLockedProducts?.some(
            (unlock) =>
              unlockedProduct.productId === unlock.productId &&
              unlock.categoryId === categoryId &&
              unlock.catalogId === catalogId,
          );
        },
      );
      if (filteredLockedProducts.length > 0) {
        dispatch(lockProduct({ lockedProducts: filteredLockedProducts }));
      }
      if (filteredRemovedLockedProducts.length > 0) {
        dispatch(
          unLockProduct({ unlockedProducts: filteredRemovedLockedProducts }),
        );
      }

      products.forEach(function (product, index) {
        var nextIndex = index + 1;
        var thisSequence = Number(product.sequence);
        var nextSequence =
          products.length !== nextIndex
            ? Number(products?.[nextIndex]?.sequence)
            : thisSequence;
        if (prevSequence >= thisSequence || thisSequence > nextSequence) {
          var rounder = Math.pow(10, 2);
          thisSequence =
            prevSequence < nextSequence
              ? (prevSequence + nextSequence) / 2
              : prevSequence + 1;
          thisSequence = Math.round(thisSequence * rounder) / rounder;
          if (prevSequence === thisSequence) {
            thisSequence = prevSequence + 1;
          }
        }
        productSequencesToUpdate.push({
          productId: product.productId,
          sequence: product.sequence,
        });
        updatedProductList.push({
          ...product,
          sequence: thisSequence,
        });
        prevSequence = thisSequence;
      });
      let unmanagedProductIds: string[] = [];
      let updatedProductIds: string[] = productIdsArray;

      if (isUnmanagedSupported) {
        unmanagedProductIds = products
          ?.filter((product) => product.position === undefined)
          ?.map((product) => product.productId);
        updatedProductIds = products
          .filter((product) => product.position !== undefined)
          ?.map((product) => product.productId);
      }
      const payload = {
        categoryId: categoryId,
        productIds: updatedProductIds,
        unmanagedProductIds,
      };
      dispatch(updateProductSequenceList(payload));
    }
    if (shouldUpdateNewPinnedProductsFlag) {
      const payload = {
        storeId: storeId || "",
        catalogId: catalogId,
        categoryId: categoryId,
        pinnedProducts: newPinnedProducts,
      };
      dispatch(updatePinnedProducts(payload));
    }
  };

  const revertProductsPosition = () => {
    dispatch(revertProductSequence());
    dispatch(resetSavedLockedUnlockedProducts());
    dispatch(resetStoredPinnedUnpinnedProductIds());
  };

  // Disable buttons if the current category is being saved
  const isCurrentCategorySaving = savingCategoryIds.includes(categoryId);
  const isDisabled = !enableSaveMode || isCurrentCategorySaving || isCategoryInitialLoading;

  return (
    <>
      <ButtonComponent
        color="secondaryButtonColorCTABlue"
        variant="outlined"
        disabled={isDisabled}
        onClick={revertProductsPosition}
      >
        {intl.formatMessage({
          id: "topPanelRevertAllButton.label",
          defaultMessage: "Revert All",
        })}
      </ButtonComponent>
      <Tooltip
        TransitionComponent={Zoom}
        placement="top"
        tooltipTitle={intl.formatMessage({
          id: "topPanelSaveButton.saveProductsLabel",
          defaultMessage: "Save Product Sequences",
        })}
      >
        <ButtonComponent
          color="primary"
          variant="contained"
          disabled={isDisabled}
          justifyContent="center"
          onClick={saveActionHandler}
        >
          {intl.formatMessage({
            id: "topPanelSaveButton.label",
            defaultMessage: "Save",
          })}
        </ButtonComponent>
      </Tooltip>
      {isLinkedCategoryFeatureEnabled && (
        <Tooltip
          TransitionComponent={Zoom}
          placement="top"
          tooltipTitle={
            enableSyncMode
              ? intl.formatMessage({
                  id: "topPanelSyncButton.syncEnabledLabel",
                  defaultMessage: "Sync Product Sequence",
                })
              : intl.formatMessage({
                  id: "topPanelSyncButton.syncDisabledLabel",
                  defaultMessage:
                    "No linked categories associated to current category",
                })
          }
          style={styles.syncButton}
        >
          <ButtonComponent
            color="primary"
            variant="contained"
            disabled={!enableSyncMode || isCurrentCategorySaving || isCategoryInitialLoading}
            justifyContent="center"
            onClick={syncActionHandler}
          >
            {intl.formatMessage({
              id: "linkedCategoriesModal.sync",
              defaultMessage: "Sync",
            })}
          </ButtonComponent>
        </Tooltip>
      )}
    </>
  );
};

export default TopPanelSaveButton;
